// Image positioning utilities for hero images

export type HeroImagePosition = 'default' | 'center' | 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'face' | 'custom' | 'crop';

/**
 * Get Cloudinary transform parameters for hero image positioning
 */
export const getHeroImageTransform = (
  publicId: string, 
  position: HeroImagePosition = 'center',
  width: number = 800,
  height: number = 600,
  zoom: number = 1.0
): string => {
  const baseUrl = `https://res.cloudinary.com/di1mzmlts/image/upload`;
  
  // Build transform parameters based on position
  let gravity = 'center'; // Default gravity
  
  switch (position) {
    case 'center':
      gravity = 'center';
      break;
    case 'left':
      gravity = 'west';
      break;
    case 'right':
      gravity = 'east';
      break;
    case 'top':
      gravity = 'north';
      break;
    case 'bottom':
      gravity = 'south';
      break;
    case 'top-left':
      gravity = 'north_west';
      break;
    case 'top-right':
      gravity = 'north_east';
      break;
    case 'bottom-left':
      gravity = 'south_west';
      break;
    case 'bottom-right':
      gravity = 'south_east';
      break;
    case 'face':
      gravity = 'face:auto'; // Cloudinary face detection
      break;
  }
  
  // Build the full transform string
  // Cloudinary zoom: z_ parameter works best with values >= 1.0 (zoom in)
  // For zoom out (< 1.0), we use larger crop dimensions instead
  
  let transforms;
  
  if (zoom !== 1.0) {
    // Two-step transformation for zoom
    // Cloudinary's z_ parameter: 1.0 = normal, 1.5 = zoom in 50%, 2.0 = zoom in 100%
    // We'll use the zoom value directly without modification for simpler behavior
    
    // Step 1: Crop with zoom and gravity
    const cropTransform = [
      `c_crop`,
      `g_${gravity}`,
      `z_${zoom.toFixed(2)}`
    ].join(',');
    
    // Step 2: Fill to final dimensions
    const fillTransform = [
      `c_fill`,
      `w_${width}`,
      `h_${height}`,
      `q_auto`,
      `f_auto`
    ].join(',');
    
    transforms = `${cropTransform}/${fillTransform}`;
  } else {
    // Single transformation without zoom
    const transformParts = [
      `c_fill`,
      `g_${gravity}`,
      `w_${width}`,
      `h_${height}`,
      `q_auto`,
      `f_auto`
    ];
    transforms = transformParts.join(',');
  }
  
  const finalUrl = `${baseUrl}/${transforms}/${publicId}`;
  
  // Debug logging
  console.log('🎨 Cloudinary Transform:', {
    position,
    gravity,
    zoom,
    width,
    height,
    transforms,
    finalUrl
  });
  
  return finalUrl;
};

/**
 * Get CSS object-position value for hero image positioning
 */
export const getCSSObjectPosition = (position: HeroImagePosition): string => {
  switch (position) {
    case 'center':
      return 'center';
    case 'left':
      return 'left center';
    case 'right':
      return 'right center';
    case 'top':
      return 'center top';
    case 'bottom':
      return 'center bottom';
    case 'top-left':
      return 'left top';
    case 'top-right':
      return 'right top';
    case 'bottom-left':
      return 'left bottom';
    case 'bottom-right':
      return 'right bottom';
    case 'face':
      return 'center'; // Fallback to center for face detection
    default:
      return 'center';
  }
};

/**
 * Get CSS background-position value for hero image positioning
 */
export const getCSSBackgroundPosition = (position: HeroImagePosition): string => {
  switch (position) {
    case 'center':
      return 'center center';
    case 'left':
      return 'left center';
    case 'right':
      return 'right center';
    case 'top':
      return 'center top';
    case 'bottom':
      return 'center bottom';
    case 'face':
      return 'center center'; // Fallback to center for face detection
    default:
      return 'center center';
  }
};

/**
 * Apply hero image positioning to a Cloudinary image URL
 */
export const applyHeroImagePositioning = (
  imageUrl: string, 
  position: HeroImagePosition = 'center',
  width?: number,
  height?: number,
  zoom?: number,
  customX?: number,
  customY?: number,
  cropBox?: { x: number; y: number; width: number; height: number }
): string => {
  // If position is default, return the original URL without any transformations
  if (position === 'default') {
    return imageUrl;
  }
  
  // If position is center, no zoom, no custom coordinates, and no crop box, return original URL
  if (position === 'center' && 
      (!zoom || zoom === 1.0) && 
      (!customX || customX === 50) && 
      (!customY || customY === 50) && 
      !cropBox) {
    return imageUrl;
  }
  
  // If it's already a Cloudinary URL, modify it with positioning
  if (imageUrl.includes('res.cloudinary.com')) {
    // Extract the public ID from the URL (everything after /upload/)
    const uploadIndex = imageUrl.indexOf('/upload/');
    if (uploadIndex === -1) {
      return imageUrl; // Not a standard Cloudinary URL
    }
    
    const publicIdWithPath = imageUrl.substring(uploadIndex + 8); // +8 to skip '/upload/'
    
    // If custom coordinates are provided, use them
    if (position === 'custom' && customX !== undefined && customY !== undefined) {
      return getHeroImageTransformWithCustomCoords(
        publicIdWithPath,
        customX,
        customY,
        zoom || 1.0,
        width,
        height
      );
    }
    
    // If crop box is provided, use crop box transformation
    if (position === 'crop' && cropBox) {
      return getHeroImageTransformWithCropBox(
        publicIdWithPath,
        cropBox.x,
        cropBox.y,
        cropBox.width,
        cropBox.height,
        width,
        height
      );
    }
    
    // Generate new URL with positioning and zoom
    return getHeroImageTransform(publicIdWithPath, position, width, height, zoom || 1.0);
  }
  
  // If it's not a Cloudinary URL, return as-is
  return imageUrl;
};

/**
 * Get positioning description for UI
 */
export const getPositionDescription = (position: HeroImagePosition): string => {
  switch (position) {
    case 'default':
      return 'Original image without positioning';
    case 'center':
      return 'Image centered in frame';
    case 'left':
      return 'Image aligned to left side';
    case 'right':
      return 'Image aligned to right side';
    case 'top':
      return 'Image aligned to top';
    case 'bottom':
      return 'Image aligned to bottom';
    case 'top-left':
      return 'Image aligned to top-left corner';
    case 'top-right':
      return 'Image aligned to top-right corner';
    case 'bottom-left':
      return 'Image aligned to bottom-left corner';
    case 'bottom-right':
      return 'Image aligned to bottom-right corner';
    case 'face':
      return 'Image positioned using face detection';
    case 'custom':
      return 'Image positioned with custom coordinates';
    case 'crop':
      return 'Image positioned using custom crop box';
    default:
      return 'Image centered in frame';
  }
};

/**
 * Get zoom description for UI
 */
export const getZoomDescription = (zoom: number): string => {
  if (zoom === 1.0) {
    return 'No zoom (original size)';
  } else if (zoom < 1.0) {
    const percentage = Math.round((1.0 - zoom) * 100);
    return `${percentage}% zoom out (wider view)`;
  } else {
    const percentage = Math.round((zoom - 1.0) * 100);
    return `${percentage}% zoom in (closer view)`;
  }
};

/**
 * Generate Cloudinary URL with custom X/Y coordinates and zoom
 * For use with the interactive hero image editor
 */
export const getHeroImageTransformWithCustomCoords = (
  publicId: string,
  x: number, // Percentage 0-100
  y: number, // Percentage 0-100
  zoom: number = 1.0,
  width: number = 800,
  height: number = 600
): string => {
  const baseUrl = `https://res.cloudinary.com/di1mzmlts/image/upload`;
  
  // Convert percentage coordinates to Cloudinary's relative offset format (-0.5 to +0.5)
  // x=0% becomes -0.5, x=50% becomes 0, x=100% becomes +0.5
  const xCoord = (x / 100 - 0.5);
  const yCoord = (y / 100 - 0.5);
  
  let transforms;
  
  if (zoom !== 1.0 || x !== 50 || y !== 50) {
    // Use center gravity with offset and zoom
    transforms = [
      `c_fill`,
      `g_center`,
      `x_${xCoord.toFixed(3)}`,  // Relative X offset (-0.5 to +0.5)
      `y_${yCoord.toFixed(3)}`,  // Relative Y offset (-0.5 to +0.5)
      `z_${zoom.toFixed(2)}`,
      `w_${width}`,
      `h_${height}`,
      `q_auto`,
      `f_auto`
    ].join(',');
    
    // Debug logging
    console.log('🎯 Custom Coords Transform:', {
      x: x, y: y, zoom: zoom,
      xCoord: xCoord, yCoord: yCoord,
      width: width, height: height,
      transforms: transforms,
      finalUrl: `${baseUrl}/${transforms}/${publicId}`
    });
  } else {
    // Default: just fill to target size
    transforms = `c_fill,w_${width},h_${height},q_auto,f_auto`;
    console.log('🎯 Default Transform (no zoom/offset):', { transforms });
  }
  
  const finalUrl = `${baseUrl}/${transforms}/${publicId}`;
  return finalUrl;
};

/**
 * Generate Cloudinary URL with crop box coordinates
 * @param publicId - Cloudinary public ID
 * @param x - Top-left X position (percentage 0-100)
 * @param y - Top-left Y position (percentage 0-100)
 * @param width - Crop box width (percentage 0-100)
 * @param height - Crop box height (percentage 0-100)
 * @param targetWidth - Final output width
 * @param targetHeight - Final output height
 */
export const getHeroImageTransformWithCropBox = (
  publicId: string,
  x: number, // Percentage 0-100
  y: number, // Percentage 0-100
  width: number, // Percentage 0-100
  height: number, // Percentage 0-100
  targetWidth: number = 800,
  targetHeight: number = 600
): string => {
  const baseUrl = `https://res.cloudinary.com/di1mzmlts/image/upload`;
  
  // Convert percentages to decimal (0-1 range)
  const xDecimal = x / 100;
  const yDecimal = y / 100;
  const widthDecimal = width / 100;
  const heightDecimal = height / 100;
  
  // Use Cloudinary's arithmetic expressions with iw (input width) and ih (input height)
  // This calculates pixel positions dynamically based on the actual image dimensions
  // Format: x_iw_mul_0.5 means "x = input_width * 0.5"
  
  // Step 1: Crop transformation
  const cropTransform = [
    `c_crop`,
    `x_iw_mul_${xDecimal.toFixed(3)}`,       // X = input_width * percentage
    `y_ih_mul_${yDecimal.toFixed(3)}`,       // Y = input_height * percentage
    `w_iw_mul_${widthDecimal.toFixed(3)}`,   // Width = input_width * percentage
    `h_ih_mul_${heightDecimal.toFixed(3)}`   // Height = input_height * percentage
  ].join(',');
  
  // Step 2: Fill transformation to target dimensions
  const fillTransform = [
    `c_fill`,
    `w_${targetWidth}`,
    `h_${targetHeight}`,
    `q_auto`,
    `f_auto`
  ].join(',');
  
  // Chain transformations with slash (/)
  const transforms = `${cropTransform}/${fillTransform}`;
  
  // Debug logging
  console.log('🎯 Crop Box Transform (Dynamic):', {
    input: { x: `${x}%`, y: `${y}%`, width: `${width}%`, height: `${height}%` },
    cloudinary: { 
      x: `iw*${xDecimal.toFixed(3)}`,
      y: `ih*${yDecimal.toFixed(3)}`,
      w: `iw*${widthDecimal.toFixed(3)}`,
      h: `ih*${heightDecimal.toFixed(3)}`
    },
    output: { targetWidth, targetHeight },
    transforms,
    finalUrl: `${baseUrl}/${transforms}/${publicId}`,
    explanation: `Crop from ${x}%,${y}% with size ${width}%x${height}% → Cloudinary crop at ${xDecimal.toFixed(3)}*iw,${yDecimal.toFixed(3)}*ih with size ${widthDecimal.toFixed(3)}*iw,${heightDecimal.toFixed(3)}*ih`
  });
  
  const finalUrl = `${baseUrl}/${transforms}/${publicId}`;
  return finalUrl;
};
